import { FieldPath, FieldValues, UseFormSetError } from "react-hook-form";

/**
 * Shared machinery every ledger panel's error mapper is built from — each
 * panel keeps its own routing table, but matching is on message *prefix*, never substring, since some errors share a phrase while meaning different things.
 */

/** Where a message starting with `prefix` belongs; `null` means the banner. */
export type PanelErrorRoute<Field extends string> = readonly [
  prefix: string,
  field: Field | null,
];

export interface MappedPanelError<Field extends string> {
  fieldErrors: Partial<Record<Field, string>>;
  panelError: string | null;
}

/**
 * Routes each message onto the first matching prefix (list specific prefixes
 * first); unmatched or `null`-routed messages fall through to the banner rather than being swallowed.
 */
export const mapPanelError = <Field extends string>(
  messages: string[],
  routes: readonly PanelErrorRoute<Field>[],
): MappedPanelError<Field> => {
  const fieldErrors: Partial<Record<Field, string>> = {};
  const panelMessages: string[] = [];

  for (const message of messages) {
    const route = routes.find(([prefix]) => message.startsWith(prefix));
    const field = route?.[1] ?? null;
    if (field === null) {
      panelMessages.push(message);
      continue;
    }
    if (!fieldErrors[field]) {
      fieldErrors[field] = message;
    }
  }

  return {
    fieldErrors,
    panelError: panelMessages.length ? panelMessages.join(" ") : null,
  };
};

/** The pair every ledger panel hands the page's submit callback. */
export interface PanelSubmitHelpers<Values extends FieldValues> {
  setError: UseFormSetError<Values>;
  setPanelError: (message: string | null) => void;
}

// This last step is identical across panels — only each one's routing table differs.
export const applyPanelError = <Values extends FieldValues>(
  { fieldErrors, panelError }: MappedPanelError<FieldPath<Values>>,
  { setError, setPanelError }: PanelSubmitHelpers<Values>,
): void => {
  for (const field of Object.keys(fieldErrors) as FieldPath<Values>[]) {
    setError(field, { type: "server", message: fieldErrors[field] });
  }
  setPanelError(panelError);
};
